import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import { InGameCurrency } from '#src/__generated__/globalTypes.ts';

import type {
  getInGameCurrencyDescription_InGameCurrencyReward,
  getInGameCurrencyDescription_probabilisticBundleSlotItemInterface,
} from './__generated__/inGameCurrencyDescription.graphql.ts';
import { isType, withFragments } from './gql/index.ts';
import { formatWord } from './humanize.ts';

type Descriptions = {
  [key in InGameCurrency]?: MessageDescriptor;
};

const descriptions: Descriptions = defineMessages({
  [InGameCurrency.COMPETITION_CRAFT_CLUE]: {
    defaultMessage: 'x{amount} Reveal Competition Clue',
  },
  [InGameCurrency.COUNTRY_CRAFT_CLUE]: {
    defaultMessage: 'x{amount} Reveal Country Clue',
  },
  [InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE]: {
    defaultMessage: 'x{amount} Reveal Best 5 Clue',
  },
  [InGameCurrency.WHEEL_TICKET]: {
    defaultMessage: 'x{amount} Wheel Ticket',
  },
  [InGameCurrency.GOLDEN_WHEEL_TICKET]: {
    defaultMessage: 'x{amount} Golden Wheel Ticket',
  },
  [InGameCurrency.SET_RAFFLE_TICKET]: {
    defaultMessage: 'x{amount} Raffle Ticket',
  },
});

export const getInGameCurrencyDescription = withFragments(
  (
    item:
      | getInGameCurrencyDescription_probabilisticBundleSlotItemInterface
      | getInGameCurrencyDescription_InGameCurrencyReward,
    formatMessage?: (
      message: MessageDescriptor,
      values: Record<string, number>
    ) => string
  ) => {
    const { currency, amount } = (() => {
      if (isType(item, 'ProbabilisticBundleSlotInGameCurrencyItem')) {
        return {
          currency: item.currency,
          amount: item.amount,
        };
      }
      if (isType(item, 'InGameCurrencyReward')) {
        return {
          currency: item.config.currency,
          amount: item.config.amount,
        };
      }
      return {};
    })();

    if (currency && amount) {
      if (descriptions[currency] && formatMessage) {
        return formatMessage(descriptions[currency], { amount });
      }
      return `${amount} ${formatWord(currency)}`;
    }
    return null;
  },
  {
    probabilisticBundleSlotItemInterface: gql`
      fragment getInGameCurrencyDescription_probabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
        id
        ... on ProbabilisticBundleSlotInGameCurrencyItem {
          id
          currency
          amount
        }
      }
    ` as TypedDocumentNode<getInGameCurrencyDescription_probabilisticBundleSlotItemInterface>,
    inGameCurrencyReward: gql`
      fragment getInGameCurrencyDescription_InGameCurrencyReward on InGameCurrencyReward {
        id
        config {
          id
          currency
          amount
        }
      }
    ` as TypedDocumentNode<getInGameCurrencyDescription_InGameCurrencyReward>,
  }
);
